import {COUNT_INCIDENT, COUNT_MONITORS, EMAIL, MAILING, RECURRING, TARIFF} from 'features/admin/lib/userFilters';
import {timezones} from 'shared/lib/timezones';

import React from 'react';
import {Controller, useForm} from 'react-hook-form';
import {useSearchParams} from 'react-router-dom';

import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import MultiSelectWithCheckbox from 'shared/ui/Input/MultiSelectWithCheckbox/MultiSelectWithCheckbox';
import OrderBySingle from 'shared/ui/OrderBySingle/OrderBySingle';
import ListFiltersForm from 'shared/ui/Modal/ListFiltersForm/ListFiltersForm';
import Select from 'shared/ui/Select/Select';
import DateRangePickerControlled from 'shared/ui/Date/DateRangePickerControlled/DateRangePickerControlled';
import {getUrlDateRange} from 'shared/lib/filterHelper';
import {getYesNoOptions} from 'shared/lib/filterOptions';
import {getUsersFilters, IUserFiltersForm, userFiltersToUrlFilters} from 'features/admin/lib/userFiltersParams';

import {t} from '@lingui/core/macro';

import styles from './UserFiltersForm.module.scss';

interface IUserFiltersFormProps {
    onClose: () => void;
}

const UserFiltersForm: React.FC<IUserFiltersFormProps> = ({onClose}) => {
    const [searchParams] = useSearchParams();
    const defaults = getUsersFilters(searchParams);

    const boolToStr = (v: boolean | undefined): string | undefined =>
        typeof v === 'boolean' ? String(v) : undefined;

    const rangeToStr = (rng?: { min?: number; max?: number } | undefined): string | undefined => {
        if (!rng) return undefined;
        const hasMin = rng.min !== undefined && rng.min !== null;
        const hasMax = rng.max !== undefined && rng.max !== null;
        if (hasMin && hasMax) return `${rng.min}-${rng.max}`;
        if (hasMin) return `${rng.min}`;
        if (hasMax) return `0-${rng.max}`;
        return undefined;
    };

    const { control, formState: { errors }, handleSubmit } = useForm<IUserFiltersForm>({
        defaultValues: {
            email: defaults.email ?? [],
            timezone: defaults.timezone,
            tariffId: defaults.tariffId ?? [],
            isRecurring: (boolToStr(defaults.isRecurring) as unknown) as any,
            agreeToNewsletter: (boolToStr(defaults.agreeToNewsletter) as unknown) as any,
            blocked: searchParams.get('blocked') ?? undefined,
            createdAt: getUrlDateRange(searchParams, 'created_at'),
            lastActivityAt: getUrlDateRange(searchParams, 'last_activity_at'),
            activeTo: getUrlDateRange(searchParams, 'active_to'),
            monitorsCountRange: (rangeToStr(defaults.monitorsCountRange) as unknown) as any,
            incidentsMonitorsCountRange: (rangeToStr(defaults.incidentsMonitorsCountRange) as unknown) as any,
            orderMonitor: (defaults.order?.filter(o => o.field === 'monitor') as any) || [],
            orderIncident: (defaults.order?.filter(o => o.field === 'incident') as any) || [],
        },
    });

    const convertIdsToEmailStatus = (ids: string[]): Array<{type: string; error: string | null}> => {
        const result: Array<{type: string; error: string | null}> = [];
        ids.forEach(id => {
            const parent = EMAIL.find(p => p.id === id);
            const child = EMAIL.flatMap(p => p.children || []).find(c => c.id === id);
            if (parent) {
                result.push({ type: parent.value, error: null });
            } else if (child) {
                const parentOfChild = EMAIL.find(p => p.children?.some(c => c.id === id));
                if (parentOfChild) {
                    result.push({
                        type: parentOfChild.value,
                        error: child.value,
                    });
                }
            }
        });

        return result;
    };

    const convertEmailStatusToIds = (filters: Array<{type: string; error: string | null}> = []): string[] => {
        return filters.flatMap(filter => {
            if (filter.error) {
                const parent = EMAIL.find(p => p.value === filter.type);
                const child = parent?.children?.find(c => c.value === filter.error);
                return child ? [child.id] : [];
            } else {
                const parent = EMAIL.find(p => p.value === filter.type);
                return parent ? [parent.id] : [];
            }
        });
    };

    return (
        <ListFiltersForm
            handleSubmit={handleSubmit}
            toFilters={values => userFiltersToUrlFilters(values, searchParams)}
            onClose={onClose}
        >
            <FormGroupWithError
                label="Email"
                error={errors?.email?.message}
                className={styles.formGroup}
            >
                <Controller
                    name="email"
                    control={control}
                    defaultValue={defaults.email ?? []}
                    render={({ field, fieldState }) => (
                        <MultiSelectWithCheckbox
                            {...field}
                            options={EMAIL}
                            value={convertEmailStatusToIds(field.value || [])}
                            onChange={(selectedIds: string[]) => {
                                field.onChange(convertIdsToEmailStatus(selectedIds));
                            }}
                            placeholder="Выберите..."
                            fieldState={fieldState}
                        />
                    )}
                />
            </FormGroupWithError>
            <FormGroupWithError
                label="Часовой пояс"
                error={errors?.timezone?.message}
                className={styles.formGroup}
            >
                <Select
                    name="timezone"
                    data={timezones.map(zone => ({value: zone, label: zone}))}
                    control={control}
                    cleanable
                />
            </FormGroupWithError>
            <FormGroupWithError
                label="Тариф"
                error={errors?.tariffId?.message}
                className={styles.formGroup}
            >
                <Controller
                    name="tariffId"
                    control={control}
                    defaultValue={defaults.tariffId ?? []}
                    render={({ field, fieldState }) => (
                        <MultiSelectWithCheckbox
                            {...field}
                            options={TARIFF}
                            value={field.value || []}
                            onChange={(selectedIds: string[]) => {
                                field.onChange(selectedIds);
                            }}
                            placeholder="Выберите тариф..."
                            fieldState={fieldState}
                        />
                    )}
                />
            </FormGroupWithError>
            <FormGroupWithError
                label="Автоплатеж"
                error={errors?.isRecurring?.message}
                className={styles.formGroup}
            >
                <Select
                    name="isRecurring"
                    data={RECURRING}
                    control={control}
                    searchable={false}
                    cleanable
                />
            </FormGroupWithError>
            <FormGroupWithError
                label="Согласие на рассылку"
                error={errors?.agreeToNewsletter?.message}
                className={styles.formGroup}
            >
                <Select
                    name="agreeToNewsletter"
                    data={MAILING}
                    control={control}
                    searchable={false}
                    cleanable
                />
            </FormGroupWithError>
            <FormGroupWithError label={t`Заблокирован`} className={styles.formGroup}>
                <Select
                    name="blocked"
                    data={getYesNoOptions()}
                    control={control}
                    searchable={false}
                    cleanable
                />
            </FormGroupWithError>
            <FormGroupWithError label={t`Дата регистрации`} className={styles.formGroup}>
                <DateRangePickerControlled control={control} name="createdAt" cleanable={true}/>
            </FormGroupWithError>
            <FormGroupWithError label={t`Последняя активность`} className={styles.formGroup}>
                <DateRangePickerControlled control={control} name="lastActivityAt" cleanable={true}/>
            </FormGroupWithError>
            <FormGroupWithError label={t`Активен до`} className={styles.formGroup}>
                <DateRangePickerControlled control={control} name="activeTo" cleanable={true}/>
            </FormGroupWithError>
            <div className={styles.containerSort}>
                <FormGroupWithError label="Кол-во мониторов: диапазон">
                    <Select
                        name="monitorsCountRange"
                        data={COUNT_MONITORS}
                        control={control}
                        searchable={false}
                        cleanable
                        menuStyle={{
                            maxHeight: '130px',
                            overflowY: 'auto',
                        }}
                    />
                </FormGroupWithError>
                <FormGroupWithError label="Сортировка">
                    <Controller
                        name="orderMonitor"
                        control={control}
                        render={({ field }) => (
                            <OrderBySingle
                                fieldName="monitor"
                                onChange={field.onChange}
                                defaultValue={field.value}
                            />
                        )}
                    />
                </FormGroupWithError>
            </div>
            <div className={styles.containerSort}>
                <FormGroupWithError label="Кол-во инцидентов: диапазон">
                    <Select
                        name="incidentsMonitorsCountRange"
                        data={COUNT_INCIDENT}
                        control={control}
                        searchable={false}
                        cleanable
                        menuStyle={{
                            maxHeight: '130px',
                            overflowY: 'auto',
                        }}
                    />
                </FormGroupWithError>
                <FormGroupWithError label="Сортировка">
                    <Controller
                        name="orderIncident"
                        control={control}
                        render={({ field }) => (
                            <OrderBySingle
                                fieldName="incident"
                                onChange={field.onChange}
                                defaultValue={field.value}
                            />
                        )}
                    />
                </FormGroupWithError>
            </div>
        </ListFiltersForm>
    );
};

export default UserFiltersForm;